/**
 * Extracts the domain name from a URL string.
 * Handles various formats: https://www.mysite.com, www.mysite.com, mysite.com, etc.
 * Returns just the domain name without protocol, www, subdomains, or TLD.
 * 
 * Examples:
 * - https://www.mysite.com -> mysite
 * - www.mysite.com -> mysite
 * - mysite.com -> mysite
 * - my-site.com -> my-site
 * - subdomain.mysite.com -> mysite
 * - api.v2.mysite.com -> mysite
 * - example.co.uk -> example
 * - www.example.com.au -> example
 * 
 * @param url - The URL string to extract domain from
 * @returns The extracted domain name
 */
export default function extractDomainName(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Remove protocol (http://, https://)
  let domain = url.replace(/^https?:\/\//i, '');
  
  // Remove www. prefix (case insensitive)
  domain = domain.replace(/^www\./i, '');
  
  // Remove trailing slash if present
  domain = domain.replace(/\/$/, '');
  
  // Remove path, query params, and fragments
  domain = domain.split('/')[0].split('?')[0].split('#')[0];
  
  // Split by dots
  const parts = domain.split('.');
  
  // Common two-part TLDs (country code TLDs)
  const twoPartTlds = [
    'co.uk', 'com.au', 'org.uk', 'net.au', 'co.nz', 'com.br',
    'co.za', 'com.mx', 'co.jp', 'com.cn', 'com.sg', 'com.hk',
    'co.in', 'com.ar', 'com.co', 'co.kr', 'com.tr', 'com.tw'
  ];
  
  // Check if the last two parts form a two-part TLD
  if (parts.length >= 3) {
    const potentialTwoPartTld = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`.toLowerCase();
    if (twoPartTlds.includes(potentialTwoPartTld)) {
      // For two-part TLDs, take the third-to-last part (the main domain)
      // Example: example.co.uk -> ['example', 'co', 'uk'] -> 'example' (index -3)
      // Example: subdomain.example.co.uk -> ['subdomain', 'example', 'co', 'uk'] -> 'example' (index -3)
      // Example: api.v2.example.co.uk -> ['api', 'v2', 'example', 'co', 'uk'] -> 'example' (index -3)
      return parts[parts.length - 3];
    }
  }
  
  // For standard TLDs or when we have subdomains
  // If we have 2 parts: mysite.com -> take first part
  if (parts.length === 2) {
    return parts[0];
  }
  
  // If we have 3+ parts (subdomain.domain.tld), take the second-to-last part
  // Example: subdomain.mysite.com -> ['subdomain', 'mysite', 'com'] -> 'mysite'
  // Example: api.v2.mysite.com -> ['api', 'v2', 'mysite', 'com'] -> 'mysite'
  if (parts.length >= 3) {
    return parts[parts.length - 2];
  }
  
  // If only one part (no dots), return as is
  return parts[0] || '';
}
